// Three-statement integrity check for the CFO forecast export.
//
// Verifies the accounting identities that make a forecast believable, over the
// SAME assembled CfoStatementData the workbook renders, so the export can both gate
// (in CI) and surface (a visible "Balanced ✓" indicator) the result:
//   - balance_identity  : Assets = Liabilities + Equity (per period/case)
//   - cf_ties_to_bs      : Cash-Flow ending cash = Balance-Sheet cash
//   - cash_continuity    : opening cash[t] = ending cash[t-1]
//
// Row matching mirrors the orchestrator's regexes so we check the exact lines the
// export presents. A missing row is skipped (not a violation). A violation requires
// BOTH abs (> $1) AND rel (> 0.5%) drift, so rounding never trips it.

import type { QuickBooksFpaStatementRender, QuickBooksFpaStatementRow } from '../../../data/ServerApiClient'
import type { CfoStatementData, CfoStatementRowData, CfoStatementType } from './cfoForecastWorkbookModel'
import type { CfoCase } from './cfoForecastDriverModel'

export type IntegrityCheckId = 'balance_identity' | 'cf_ties_to_bs' | 'cash_continuity'

export type IntegrityViolation = {
  check: IntegrityCheckId
  statementType: CfoStatementType
  case: CfoCase
  periodIndex: number
  period: string
  expected: number
  actual: number
  absDiff: number
  relDiff: number
}

export type IntegrityReport = {
  ok: boolean
  /** Worst absolute drift across ALL comparisons (≈0 in a balanced book) — drives the indicator. */
  worstAbsDiff: number
  worstRelDiff: number
  worstViolation: IntegrityViolation | null
  violations: IntegrityViolation[]
  toleranceAbs: number
  toleranceRel: number
}

export type IntegrityOptions = {
  toleranceAbs?: number
  toleranceRel?: number
  /** Which value cases to check. Default ['expected'] (the band the indicator reads). */
  cases?: CfoCase[]
}

export type ProfitAndLossFormulaCheckId = 'gross_profit' | 'operating_income' | 'net_income'

export type ProfitAndLossFormulaViolation = {
  check: ProfitAndLossFormulaCheckId
  periodIndex: number
  period: string
  expected: number
  actual: number
  absDiff: number
  relDiff: number
}

export type ProfitAndLossFormulaIntegrityReport = {
  ok: boolean
  violations: ProfitAndLossFormulaViolation[]
  worstViolation: ProfitAndLossFormulaViolation | null
  toleranceAbs: number
  toleranceRel: number
}

export type ProfitAndLossFormulaIntegrityOptions = {
  toleranceAbs?: number
  toleranceRel?: number
}

const fin = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

const normalizedLabel = (value: unknown): string =>
  String(value || '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ')

const renderRowValue = (row: QuickBooksFpaStatementRow | null | undefined, index: number): number | null => {
  const raw = row?.values?.[index]
  // Artifact-backed statements can carry a synthetic section-total row whose
  // forecast cell is intentionally blank while its account leaves are present.
  // A blank is "not available", not zero: Number(null) === 0 would otherwise
  // manufacture an integrity violation and hide an otherwise usable statement.
  if (raw == null) return null
  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

const renderRowMagnitude = (row: QuickBooksFpaStatementRow): number =>
  (row.values || []).reduce<number>((sum, value) => {
    const numeric = Number(value)
    return Number.isFinite(numeric) ? sum + Math.abs(numeric) : sum
  }, 0)

const findRenderRowByLabels = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  labelSets: string[][],
): QuickBooksFpaStatementRow | null => {
  const rows = statement?.rows || []
  const bestMatch = (matches: QuickBooksFpaStatementRow[]): QuickBooksFpaStatementRow | null => {
    const best = matches.sort((a, b) => {
      const score = (row: QuickBooksFpaStatementRow) => renderRowMagnitude(row)
        + (row.is_total || /^total\s+/.test(normalizedLabel(row.label)) ? 100_000_000 : 0)
        + (row.is_formula ? 10_000_000 : 0)
        + ((row.row_type === 'group' || row.row_type === 'formula') ? 1_000_000 : 0)
        + (Number(row.level || 0) === 0 ? 100_000 : 0)
      return score(b) - score(a)
    })[0]
    return best || null
  }

  // Resolve exact canonical control rows across every synonym before allowing
  // fuzzy substring matches. Otherwise "Total AOX Revenue" satisfies the first
  // [total, revenue] synonym and wins before the canonical "Total Income" row
  // is considered, even though the former is only a nested revenue subtotal.
  for (const labels of labelSets) {
    const exactLabel = labels.map(normalizedLabel).join(' ')
    const best = bestMatch(rows.filter((row) => {
      const label = normalizedLabel(row.label)
      return label === exactLabel || label === `total ${exactLabel}`
    }))
    if (best) return best
  }

  for (const labels of labelSets) {
    const wanted = labels.map(normalizedLabel)
    const best = bestMatch(rows.filter((row) => {
      const label = normalizedLabel(row.label)
      return wanted.every((part) => label.includes(part))
    }))
    if (best) return best
  }
  return null
}

export function checkProfitAndLossRenderFormulaIntegrity(
  statement: QuickBooksFpaStatementRender | null | undefined,
  options: ProfitAndLossFormulaIntegrityOptions = {},
): ProfitAndLossFormulaIntegrityReport {
  const toleranceAbs = options.toleranceAbs ?? 1
  const toleranceRel = options.toleranceRel ?? 0.005
  if (statement?.statement_type !== 'profit_and_loss') {
    return { ok: true, violations: [], worstViolation: null, toleranceAbs, toleranceRel }
  }
  const revenue = findRenderRowByLabels(statement, [['total', 'revenue'], ['total', 'income'], ['revenue'], ['income']])
  const cogs = findRenderRowByLabels(statement, [['total', 'cost', 'of', 'goods'], ['total', 'cost', 'of', 'sales'], ['cost', 'of', 'goods'], ['cost', 'of', 'sales'], ['cogs']])
  const grossProfit = findRenderRowByLabels(statement, [['gross', 'profit']])
  const expenses = findRenderRowByLabels(statement, [['total', 'expenses'], ['total', 'expense'], ['operating', 'expenses'], ['expenses'], ['expense']])
  const operatingIncome = findRenderRowByLabels(statement, [['net', 'operating', 'income'], ['operating', 'income']])
  const netOtherIncome = findRenderRowByLabels(statement, [['net', 'other', 'income'], ['other', 'income', 'expenses'], ['other', 'income', 'and', 'expenses']])
  const otherIncome = findRenderRowByLabels(statement, [['total', 'other', 'income'], ['other', 'income']])
  const otherExpenses = findRenderRowByLabels(statement, [['total', 'other', 'expenses'], ['total', 'other', 'expense'], ['other', 'expenses'], ['other', 'expense']])
  const netIncome = findRenderRowByLabels(statement, [['net', 'income'], ['net', 'earnings']])
  const violations: ProfitAndLossFormulaViolation[] = []

  const consider = (check: ProfitAndLossFormulaCheckId, periodIndex: number, expected: number | null, actual: number | null) => {
    if (expected == null || actual == null) return
    const absDiff = Math.abs(actual - expected)
    const relDiff = absDiff / Math.max(1, Math.abs(expected))
    if (absDiff > toleranceAbs && relDiff > toleranceRel) {
      violations.push({
        check,
        periodIndex,
        period: statement.periods?.[periodIndex] || '',
        expected,
        actual,
        absDiff,
        relDiff,
      })
    }
  }

  const periodCount = statement.periods?.length || Math.max(0, ...statement.rows.map((row) => row.values?.length || 0))
  for (let index = 0; index < periodCount; index += 1) {
    const revenueValue = renderRowValue(revenue, index)
    const cogsValue = renderRowValue(cogs, index)
    const expenseValue = renderRowValue(expenses, index)
    const grossExpected = revenueValue != null && cogsValue != null ? revenueValue - cogsValue : null
    consider('gross_profit', index, grossExpected, renderRowValue(grossProfit, index))

    const operatingExpected = grossExpected != null && expenseValue != null ? grossExpected - expenseValue : null
    consider('operating_income', index, operatingExpected, renderRowValue(operatingIncome, index))

    let otherValue = renderRowValue(netOtherIncome, index)
    if (otherValue == null) {
      const incomeValue = renderRowValue(otherIncome, index)
      const expenseOtherValue = renderRowValue(otherExpenses, index)
      if (incomeValue != null || expenseOtherValue != null) otherValue = Number(incomeValue || 0) - Number(expenseOtherValue || 0)
    }
    const netExpected = operatingExpected != null ? operatingExpected + Number(otherValue || 0) : null
    consider('net_income', index, netExpected, renderRowValue(netIncome, index))
  }

  const worstViolation = violations.reduce<ProfitAndLossFormulaViolation | null>(
    (worst, violation) => (worst == null || violation.absDiff > worst.absDiff ? violation : worst),
    null,
  )

  return {
    ok: violations.length === 0,
    violations,
    worstViolation,
    toleranceAbs,
    toleranceRel,
  }
}

const findRowBy = (
  s: CfoStatementData | undefined,
  pred: (r: CfoStatementRowData) => boolean,
  preferTotal = true,
): CfoStatementRowData | undefined => {
  if (!s) return undefined
  const matches = s.rows.filter(pred)
  if (!matches.length) return undefined
  return preferTotal ? (matches.find((r) => r.isTotal) ?? matches[0]) : matches[0]
}

const isCashRow = (r: CfoStatementRowData) =>
  /(total\s+)?(bank|cash and cash|cash)\b/i.test(r.label) || r.nodeId === 'fg-forecast-cash-ending-cash'
const isCfEnding = (r: CfoStatementRowData) =>
  r.nodeId === 'fg-forecast-cash-ending-cash' || (/ending|closing/i.test(r.label) && /cash|balance/i.test(r.label))
const isCfBeginning = (r: CfoStatementRowData) =>
  r.nodeId === 'fg-forecast-cash-beginning-cash' || (/beginning|opening/i.test(r.label) && /cash|balance/i.test(r.label))

export function checkThreeStatementIntegrity(
  statements: CfoStatementData[],
  options: IntegrityOptions = {},
): IntegrityReport {
  const toleranceAbs = options.toleranceAbs ?? 1
  const toleranceRel = options.toleranceRel ?? 0.005
  const cases = options.cases ?? (['expected'] as CfoCase[])
  const bs = statements.find((s) => s.statementType === 'balance_sheet')
  const cf = statements.find((s) => s.statementType === 'cash_flow')

  const violations: IntegrityViolation[] = []
  let worstAbsDiff = 0
  let worstRelDiff = 0

  const consider = (
    check: IntegrityCheckId,
    statementType: CfoStatementType,
    c: CfoCase,
    i: number,
    period: string,
    expected: number,
    actual: number,
  ) => {
    const absDiff = Math.abs(actual - expected)
    const relDiff = absDiff / Math.max(1, Math.abs(expected))
    if (absDiff > worstAbsDiff) worstAbsDiff = absDiff
    if (relDiff > worstRelDiff) worstRelDiff = relDiff
    if (absDiff > toleranceAbs && relDiff > toleranceRel) {
      violations.push({ check, statementType, case: c, periodIndex: i, period, expected, actual, absDiff, relDiff })
    }
  }

  // balance_identity: Total Assets = Total Liabilities and Equity
  const assetsRow = findRowBy(bs, (r) => /total\s+assets/i.test(r.label))
  const leRow = findRowBy(bs, (r) => /total\s+liabilities\s+and\s+equity/i.test(r.label))
  const totalLiabRow = findRowBy(bs, (r) => /total\s+liabilities\b/i.test(r.label) && !/equity/i.test(r.label))
  const totalEquityRow = findRowBy(bs, (r) => /total\s+equity/i.test(r.label))
  if (bs && assetsRow) {
    bs.periods.forEach((period, i) => {
      for (const c of cases) {
        const assets = fin(assetsRow.byCase[c]?.[i])
        let le = leRow ? fin(leRow.byCase[c]?.[i]) : null
        if (le == null && totalLiabRow && totalEquityRow) {
          const l = fin(totalLiabRow.byCase[c]?.[i])
          const e = fin(totalEquityRow.byCase[c]?.[i])
          if (l != null && e != null) le = l + e
        }
        if (assets != null && le != null) consider('balance_identity', 'balance_sheet', c, i, period, le, assets)
      }
    })
  }

  // cf_ties_to_bs: CF ending cash = BS cash (per period, aligned by index)
  const cfEndRow = findRowBy(cf, isCfEnding)
  const bsCashRow = findRowBy(bs, isCashRow)
  if (cf && cfEndRow && bs && bsCashRow) {
    const n = Math.min(cf.periods.length, bs.periods.length)
    for (let i = 0; i < n; i += 1) {
      for (const c of cases) {
        const cfv = fin(cfEndRow.byCase[c]?.[i])
        const bsv = fin(bsCashRow.byCase[c]?.[i])
        if (cfv != null && bsv != null) consider('cf_ties_to_bs', 'cash_flow', c, i, cf.periods[i], bsv, cfv)
      }
    }
  }

  // cash_continuity: opening cash[t] = ending cash[t-1] (within the CF statement)
  const cfBeginRow = findRowBy(cf, isCfBeginning)
  if (cf && cfBeginRow && cfEndRow) {
    for (let i = 1; i < cf.periods.length; i += 1) {
      for (const c of cases) {
        const begin = fin(cfBeginRow.byCase[c]?.[i])
        const prevEnd = fin(cfEndRow.byCase[c]?.[i - 1])
        if (begin != null && prevEnd != null) consider('cash_continuity', 'cash_flow', c, i, cf.periods[i], prevEnd, begin)
      }
    }
  }

  const worstViolation = violations.reduce<IntegrityViolation | null>(
    (worst, v) => (worst == null || v.absDiff > worst.absDiff ? v : worst),
    null,
  )

  return {
    ok: violations.length === 0,
    worstAbsDiff,
    worstRelDiff,
    worstViolation,
    violations,
    toleranceAbs,
    toleranceRel,
  }
}
